import { buildProjectId, buildProjectName } from './projectIdentity.js';
import {
  buildProjectProjection,
  mergeProjectProjection,
  resolveProjectRecordId,
} from './projectProjection.js';

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);
const callHandler = (handler, ...args) => (typeof handler === 'function' ? handler(...args) : undefined);

/**
 * Create project lifecycle actions for chat-oriented orchestration hosts.
 *
 * @returns {{
 *  onCreateProject: (options?: {projectName?: string}) => Promise<void>,
 *  onProjectSelect: (project?: Record<string, unknown>) => void,
 *  onRenameProject: (project?: Record<string, unknown>, nextName?: string) => Promise<void>,
 *  onArchiveProject: (project?: Record<string, unknown>) => Promise<void>,
 }}
 */
export function createProjectActions({
  projectAPI,
  projectId,
  projectItems,
  defaultProjectName,
  loadProjectList,
  onProjectSelect,
  resetSession,
  setProjectItems,
} = {}) {
  const resolveCurrentProjectId = () => String(projectId || '').trim();

  return {
    onCreateProject: async (options) => {
      if (!Object(projectAPI).upsertProject) {
        return;
      }

      const resolvedProjectId = buildProjectId();
      const projectName = buildProjectName(
        defaultProjectName,
        projectItems,
        Object(options).projectName,
      );
      const upsertedProject = await projectAPI.upsertProject(resolvedProjectId, {
        project_name: projectName,
        status: 'active',
      });
      const projectedProject = buildProjectProjection(
        upsertedProject,
        resolvedProjectId,
        projectName,
      );

      callHandler(setProjectItems, (currentItems) => (
        mergeProjectProjection(currentItems, projectedProject)
      ));

      callHandler(onProjectSelect, projectedProject);
      callHandler(resetSession);

      const latestProjects = await loadProjectList();
      const createdProjectId = resolveProjectRecordId(projectedProject);
      const listHasCreatedProject = latestProjects?.some((project) => (
        resolveProjectRecordId(project) === createdProjectId
      )) === true;
      if (!listHasCreatedProject) {
        callHandler(setProjectItems, (currentItems) => mergeProjectProjection(currentItems, projectedProject));
      }
    },

    onProjectSelect: (project) => {
      const resolvedProject = resolveProjectRecordId(project);
      const projectedProject = buildProjectProjection(
        project,
        resolvedProject,
        defaultProjectName,
      );

      if (resolvedProject) {
        setProjectItems?.((currentItems) => mergeProjectProjection(currentItems, projectedProject));
      }

      onProjectSelect?.(projectedProject);
      resetSession?.();
    },

    onRenameProject: async (project, nextName) => {
      const resolvedProjectId = String(Object(project).project_id || '').trim();
      const projectName = String(nextName || '').trim();
      if (!resolvedProjectId || !projectName || !Object(projectAPI).upsertProject) {
        return;
      }
      await projectAPI.upsertProject(resolvedProjectId, {
        project_name: projectName,
        status: String(Object(project).status || 'active').trim() || 'active',
      });
      await loadProjectList();
    },

    onArchiveProject: async (project) => {
      const resolvedProjectId = String(firstTruthy(() => (Object(project).project_id), () => (''))).trim();
      if (firstTruthy(() => (!resolvedProjectId), () => (!Object(projectAPI).upsertProject))) {
        return;
      }
      await projectAPI.upsertProject(resolvedProjectId, {
        project_name: firstTruthy(() => (String(Object(project).name || Object(project).project_name || '').trim()), () => (null)),
        status: 'archived',
      });

      const latestProjects = await loadProjectList();
      if (resolvedProjectId !== resolveCurrentProjectId()) {
        return;
      }

      resetSession?.();
      const nextProject = Array.isArray(latestProjects) ? latestProjects[0] : null;
      onProjectSelect?.(firstTruthy(() => (nextProject), () => ({ project_id: '', key: '' })));
    },
  };
}
